import React, { useState } from 'react';
import { Video, Mic, Shield, X, ExternalLink, Camera } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import api from '../utils/api';

interface VideoConsentModalProps {
  isOpen: boolean;
  onClose: () => void;
  onAccept: () => void;
  onCancel?: () => void;
  blockType: 'video_qa' | 'video_simulation';
  interviewId?: number;
  blockId?: number;
  inviteToken?: string | null;
}

const VideoConsentModal: React.FC<VideoConsentModalProps> = ({
  isOpen,
  onClose,
  onAccept,
  onCancel,
  blockType,
  interviewId,
  blockId,
  inviteToken
}) => {
  const { t } = useTranslation();
  const [consents, setConsents] = useState({
    video: false,
    audio: false,
    ai_analysis: false,
    data_processing: false
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const allConsentsGiven = Object.values(consents).every(Boolean);

  const handleConsentChange = (type: keyof typeof consents) => {
    setConsents(prev => ({
      ...prev,
      [type]: !prev[type]
    }));
  };

  const handleAccept = async () => {
    if (!allConsentsGiven) {
      setError(t('videoConsent.allRequired'));
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const baseMetadata = {
        block_type: blockType,
        interview_id: interviewId,
        block_id: blockId,
        timestamp: new Date().toISOString()
      };

      await api.post(`/api/interviews/${interviewId}/consents`, {
        invite_token: inviteToken || undefined,
        consents: [
          { consent_type: 'video_recording', consent_metadata: baseMetadata },
          { consent_type: 'audio_recording', consent_metadata: baseMetadata },
          { consent_type: 'ai_analysis', consent_metadata: baseMetadata },
          { consent_type: 'data_processing', consent_metadata: baseMetadata }
        ]
      });

      // LocalStorage'a kaydet (gelecekteki kontrol için)
      localStorage.setItem('video-consent', 'accepted');
      localStorage.setItem('video-consent-date', new Date().toISOString());

      onAccept();
    } catch (err: any) {
      setError(t('videoConsent.saveError'));
      console.error('Video consent error:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleReject = () => {
    if (onCancel) {
      onCancel();
      return;
    }
    onClose();
  };

  const handleLinkClick = (e: React.MouseEvent) => {
    e.preventDefault();
    window.open('/legal/kvkk-aydinlatma', '_blank');
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full p-6 max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <Camera className="text-lotus-gold" size={24} />
            <h3 className="text-xl font-semibold text-gray-900">
              {t('videoConsent.title')}
            </h3>
          </div>
          <button
            onClick={handleReject}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X size={20} />
          </button>
        </div>

        {/* Description */}
        <p className="text-gray-600 mb-6 leading-relaxed">
          {t('videoConsent.description')}
        </p>

        {/* Video Recording Consent */}
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
          <div className="flex items-center gap-3 mb-3">
            <Video className="text-blue-600" size={20} />
            <span className="font-medium text-blue-900">{t('videoConsent.videoTitle')}</span>
          </div>
          <p className="text-sm text-gray-700 mb-3">
            {t('videoConsent.videoDescription')}
          </p>
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={consents.video}
              onChange={() => handleConsentChange('video')}
              className="w-4 h-4 text-blue-600 rounded"
            />
            <span className="text-sm text-gray-700">{t('videoConsent.videoAccept')}</span>
          </label>
        </div>

        {/* Audio Recording Consent */}
        <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-4">
          <div className="flex items-center gap-3 mb-3">
            <Mic className="text-green-600" size={20} />
            <span className="font-medium text-green-900">{t('videoConsent.audioTitle')}</span>
          </div>
          <p className="text-sm text-gray-700 mb-3">
            {t('videoConsent.audioDescription')}
          </p>
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={consents.audio}
              onChange={() => handleConsentChange('audio')}
              className="w-4 h-4 text-green-600 rounded"
            />
            <span className="text-sm text-gray-700">{t('videoConsent.audioAccept')}</span>
          </label>
        </div>

        {/* AI Analysis Consent */}
        <div className="bg-purple-50 border border-purple-200 rounded-lg p-4 mb-4">
          <div className="flex items-center gap-3 mb-3">
            <Shield className="text-purple-600" size={20} />
            <span className="font-medium text-purple-900">{t('videoConsent.aiTitle')}</span>
          </div>
          <p className="text-sm text-gray-700 mb-3">
            {t('videoConsent.aiDescription')}
          </p>
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={consents.ai_analysis}
              onChange={() => handleConsentChange('ai_analysis')}
              className="w-4 h-4 text-purple-600 rounded"
            />
            <span className="text-sm text-gray-700">{t('videoConsent.aiAccept')}</span>
          </label>
        </div>

        {/* Data Processing Consent */}
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-4">
          <div className="flex items-center gap-3 mb-3">
            <Shield className="text-amber-600" size={20} />
            <span className="font-medium text-amber-900">{t('videoConsent.dataTitle')}</span>
          </div>
          <p className="text-sm text-gray-700 mb-3">
            {t('videoConsent.dataDescription')}
          </p>
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={consents.data_processing}
              onChange={() => handleConsentChange('data_processing')}
              className="w-4 h-4 text-amber-600 rounded"
            />
            <span className="text-sm text-gray-700">{t('videoConsent.dataAccept')}</span>
          </label>
        </div>

        {/* Rights Information */}
        <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 mb-6">
          <h4 className="font-medium text-gray-900 mb-3">{t('videoConsent.rightsTitle')}</h4>
          <ul className="text-sm text-gray-600 space-y-1">
            {(t('videoConsent.rightsItems', { returnObjects: true }) as string[]).map((item) => (
              <li key={item}>• {item}</li>
            ))}
          </ul>
        </div>

        {/* KVKK Link */}
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
          <div className="flex items-center gap-2 mb-2">
            <ExternalLink className="text-blue-600" size={16} />
            <span className="text-sm font-medium text-blue-900">
              {t('videoConsent.detailsTitle')}
            </span>
          </div>
          <p className="text-sm text-gray-700 mb-3">
            {t('videoConsent.detailsDescription')}
          </p>
          <a
            href="/legal/kvkk-aydinlatma"
            target="_blank"
            rel="noopener noreferrer"
            onClick={handleLinkClick}
            className="inline-flex items-center gap-2 text-sm text-blue-600 hover:text-blue-800 underline"
          >
            {t('videoConsent.readKvkk')}
            <ExternalLink size={14} />
          </a>
        </div>

        {/* Error Message */}
        {error && (
          <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-red-600 text-sm">
            {error}
          </div>
        )}

        {/* Action Buttons */}
        <div className="flex gap-3">
          <button
            onClick={handleAccept}
            disabled={!allConsentsGiven || loading}
            className="flex-1 bg-lotus-gold text-white px-6 py-3 rounded-lg hover:bg-lotus-gold/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed font-medium"
          >
            {loading ? t('videoConsent.processing') : t('videoConsent.acceptAndStart')}
          </button>
          <button
            onClick={handleReject}
            disabled={loading}
            className="flex-1 bg-gray-200 text-gray-700 px-6 py-3 rounded-lg hover:bg-gray-300 transition-colors disabled:opacity-50 font-medium"
          >
            {t('videoConsent.cancel')}
          </button>
        </div>

        {/* Footer Info */}
        <div className="mt-4 pt-4 border-t border-gray-200">
          <p className="text-xs text-gray-500 text-center">
            {t('videoConsent.footer')}
          </p>
        </div>
      </div>
    </div>
  );
};

export default VideoConsentModal;
